import { useTranslation } from 'react-i18next';
import { ScrollContainer } from 'react-indiana-drag-scroll';
import { useNavigate } from 'react-router';

import { paths } from '@/app/router/paths';
import { KpiDialogEditor } from '@/features/projects/create-kpi/components/kpi-dialog-editor';
import KPICard from '@/features/projects/project-details/components/kpi-card';
import { KPICardSkeleton } from '@/features/projects/project-details/components/kpi-card-skeleton';
import { NavigationButton } from '@/features/projects/project-details/components/navigation-button';
import { getKpiUniqueId } from '@/features/projects/shared/utils/kpi-utils';
import PlusIcon from '@/shared/assets/plus.svg?react';
import { PlaceholderButton } from '@/shared/components/actions/placeholder-button';
import { NoEntriesCardPlaceholder } from '@/shared/components/placeholders/no-entries-card-placeholder';
import { Section } from '@/shared/components/section/section';
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
} from '@/shared/components/section/section-header';
import { Button } from '@/shared/components/ui/button';
import 'react-indiana-drag-scroll/dist/style.css';
import { KpiWithMetrics, ProjectType } from '@/shared/types/api';

interface ProjectDetailsKpisProps {
  projectType: ProjectType;
  projectId: number;
  kpis: KpiWithMetrics[];
  isKpisPending: boolean;
}

export const ProjectDetailsKpis = ({
  projectType,
  projectId,
  kpis,
  isKpisPending,
}: ProjectDetailsKpisProps) => {
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });
  const navigate = useNavigate();

  const hasKpis = (kpis ?? []).length > 0;

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{t('kpis')}</SectionTitle>
        <SectionActions>
          <KpiDialogEditor projectType={projectType}>
            <Button variant={'greenGradient'} btnType="secondary">
              <PlusIcon />
              {t('createKpi')}
            </Button>
          </KpiDialogEditor>
          <NavigationButton
            onClick={() => {
              navigate(
                paths.insights.getHref({
                  projectId: projectId.toString(),
                }),
              );
            }}
          />
        </SectionActions>
      </SectionHeader>
      <ScrollContainer mouseScroll={{ ignoreElements: 'input' }}>
        <div className="flex flex-row gap-8">
          {isKpisPending
            ? Array.from({ length: 5 }).map((_, index) => (
                <KPICardSkeleton key={index} />
              ))
            : kpis?.map((kpi) => (
                <KPICard
                  key={getKpiUniqueId(kpi)}
                  kpi={kpi}
                  projectType={projectType}
                  projectId={projectId}
                />
              ))}

          {!isKpisPending && !hasKpis && (
            <NoKPIPlaceholder projectType={projectType} />
          )}
        </div>
      </ScrollContainer>
      {/* TODO unmock. Is this even needed? */}
      {/* <PageIndicator className="self-center" count={4} currentPage={0} /> */}
    </Section>
  );
};

const NoKPIPlaceholder = ({ projectType }: { projectType: ProjectType }) => {
  const { t } = useTranslation('project', { keyPrefix: 'NoKPIPlaceholder' });

  return (
    <NoEntriesCardPlaceholder title={t('title')} description={t('description')}>
      <KpiDialogEditor projectType={projectType}>
        <PlaceholderButton />
      </KpiDialogEditor>
    </NoEntriesCardPlaceholder>
  );
};
